import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Users, FileText, Settings, Sparkles, Copy, Mail, Shield, Loader2, Bell, MessageSquare, Eye, EyeOff } from 'lucide-react';
import { getSchoolCredentials, SchoolCredentials, getOnboardingState, saveOnboardingState } from '@/lib/onboarding-store';
import { registerSchool, inviteTeacher, sendSecondaryContactInvite } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { isDemoMode } from '@/lib/demo/store';

interface SetupCompleteProps {
  schoolName: string;
  staffCount: number;
  onContinue: () => void;
}

export function SetupComplete({ schoolName, staffCount, onContinue }: SetupCompleteProps) {
  const [schoolCreds, setSchoolCreds] = useState<SchoolCredentials | null>(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [inviteWarnings, setInviteWarnings] = useState<string[]>([]);
  const [readyToContinue, setReadyToContinue] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const creds = getSchoolCredentials();
    setSchoolCreds(creds);
  }, []);

  // Land at the top of the page so the confetti (which falls from y=0) is
  // visible immediately, instead of wherever the previous wizard step left
  // the scroll position.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Confetti animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const COLORS = ['#6366f1','#22c55e','#f59e0b','#ec4899','#3b82f6','#a855f7','#14b8a6','#f97316'];
    const TOTAL = 160;

    type Piece = {
      x: number; y: number; r: number; d: number;
      color: string; tilt: number; tiltAngle: number; tiltSpeed: number;
      shape: 'rect' | 'circle';
    };

    const pieces: Piece[] = Array.from({ length: TOTAL }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height - canvas.height,
      r: Math.random() * 8 + 4,
      d: Math.random() * TOTAL + 30,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      tilt: Math.floor(Math.random() * 10) - 10,
      tiltAngle: 0,
      tiltSpeed: Math.random() * 0.1 + 0.05,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    }));

    let angle = 0;
    let rafId: number;
    let elapsed = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      angle += 0.01;
      elapsed++;

      pieces.forEach((p) => {
        p.tiltAngle += p.tiltSpeed;
        p.y += (Math.cos(angle + p.d) + 2.5) * 0.9;
        p.x += Math.sin(angle) * 0.6;
        p.tilt = Math.sin(p.tiltAngle) * 12;

        // Fade out after 220 frames
        const alpha = elapsed > 180 ? Math.max(0, 1 - (elapsed - 180) / 80) : 1;
        ctx.globalAlpha = alpha;

        ctx.beginPath();
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.tilt * Math.PI / 180);
          ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
          ctx.restore();
        }
        ctx.fill();

        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
        }
      });

      ctx.globalAlpha = 1;
      if (elapsed < 260) rafId = requestAnimationFrame(draw);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    // Short delay so the entrance animation plays first
    const timer = setTimeout(() => { rafId = requestAnimationFrame(draw); }, 400);

    const onResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handleGoToDashboard = async () => {
    if (readyToContinue) { onContinue(); return; }
    if (!schoolCreds) {
      onContinue();
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      // Register the school admin account
      const { userId, hashedToken } = await registerSchool(schoolCreds.email, schoolCreds.password, schoolName);

      // Sign in right away — moved ahead of the invite calls (it used to run
      // after them) because sendSecondaryContactInvite requires a real
      // Bearer token: it derives schoolId from the JWT rather than trusting
      // a client-supplied value, so a session has to exist before we can
      // call it. Try verifyOtp first (skips propagation lag); fall back to
      // signInWithPassword with retries for Supabase propagation lag on a
      // freshly-created account.
      let adminAccessToken: string | undefined;
      /**
       * Demo has no Supabase account to sign into.
       *
       * registerSchool is mocked in demo and returns a userId with no
       * hashedToken, so the verifyOtp branch is skipped and the fallback loop
       * below ran against REAL Supabase with credentials that do not exist:
       * six attempts, five one-second sleeps between them, six network
       * round-trips, then a thrown error. That is the ten-to-twenty-second
       * spinner on the completion screen — the "loading timeout after finishing
       * the wizard" — and it happened every single time, because nothing in
       * this file was demo-aware.
       *
       * Every API call after this point goes through the demo mock, which never
       * inspects the token, so a placeholder is sufficient and honest: there is
       * no session because there is no account.
       */
      if (isDemoMode()) {
        adminAccessToken = 'demo-session';
      } else if (hashedToken) {
        const { data, error: otpError } = await supabase.auth.verifyOtp({
          type: 'magiclink',
          token_hash: hashedToken,
        });
        if (!otpError) adminAccessToken = data.session?.access_token;
      }
      if (!adminAccessToken && !isDemoMode()) {
        let lastSignInError: string | null = null;
        for (let attempt = 0; attempt < 6; attempt++) {
          if (attempt > 0) await new Promise(r => setTimeout(r, 1000));
          const { data, error } = await supabase.auth.signInWithPassword({
            email: schoolCreds.email,
            password: schoolCreds.password,
          });
          if (!error) { adminAccessToken = data.session?.access_token; break; }
          lastSignInError = error.message;
        }
        if (!adminAccessToken) throw new Error(`Could not sign in after registration: ${lastSignInError}`);
      }

      // Send invite emails to all teachers — use allSettled so a single failed invite
      // (e.g. email already registered as school_admin → 409) never blocks wizard completion.
      const onboardingData = getOnboardingState();
      const staff = onboardingData.schoolData.staff ?? [];
      /**
       * Demo sends no invites.
       *
       * They cost a round-trip each and every one of them is a no-op the demo
       * user can neither see nor act on — the invited teacher does not exist, and
       * the mailbox already carries a copy of what such an email says. All they
       * produced on the completion screen was a wall of per-recipient rows
       * crowding out the thing that matters, which is getting into the product.
       * The count is stated instead.
       */
      const demo = isDemoMode();
      const teacherResults = demo
        ? []
        : await Promise.allSettled(
            staff.map(member =>
              inviteTeacher(member.email, member.firstName, member.lastName, userId, schoolName),
            ),
          );

      const warnings: string[] = [];
      if (!demo) staff.forEach((member, i) => {
        const result = teacherResults[i];
        if (result.status === 'rejected') {
          const msg = result.reason instanceof Error ? result.reason.message : String(result.reason);
          // 409 means the email belongs to a non-teacher account — surface a clear message
          if (msg.includes('409') || msg.includes('already registered')) {
            warnings.push(`${member.email}, already registered with a different role. Please send the invite manually from the Staff tab.`);
          } else {
            warnings.push(`${member.email}, invite failed. You can resend it from the Staff tab.`);
          }
        }
      });

      // Send invite emails to secondary contacts — also non-blocking
      const secondaryContacts = onboardingData.schoolData.secondaryContacts ?? [];
      const contactResults = demo
        ? []
        : await Promise.allSettled(
            secondaryContacts
              .filter(c => c.email)
              .map(c =>
                sendSecondaryContactInvite(adminAccessToken, {
                  email: c.email,
                  name: c.name,
                  role: c.role,
                  schoolName,
                }),
              ),
          );
      if (!demo) secondaryContacts.filter(c => c.email).forEach((c, i) => {
        const result = contactResults[i];
        if (result?.status === 'rejected') {
          const errMsg = result.reason instanceof Error ? result.reason.message : String(result.reason ?? 'unknown error');
          warnings.push(`${c.email} (secondary contact), invite failed: ${errMsg}. You can resend from Settings.`);
        }
      });

      saveOnboardingState({ isComplete: true, userType: 'school', supabaseUserId: userId });

      if (warnings.length > 0) {
        // Show warnings and let the admin acknowledge before navigating away
        setInviteWarnings(warnings);
        setIsSubmitting(false);
        setReadyToContinue(true);
      } else {
        onContinue();
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const copyPassword = (password: string) => {
    navigator.clipboard.writeText(password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const features = [
    { icon: Users,         label: 'View and manage your teaching staff' },
    { icon: Bell,          label: 'Review and approve pending cover lessons' },
    { icon: MessageSquare, label: 'See feedback left by cover teachers' },
    { icon: FileText,      label: 'Browse all lessons submitted by your staff' },
    { icon: Settings,      label: 'Update school settings and contacts' },
  ];

  return (
    <div className="min-h-screen bg-background paper-dots flex items-center justify-center p-4 md:p-8">
      <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-50" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        {/* Success icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/10 flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: 'spring' }}
          >
            <Check className="w-10 h-10 text-success" />
          </motion.div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            You're all set!
          </h1>
          <p className="text-lg text-muted-foreground">
            <span className="font-semibold text-foreground">{schoolName}</span> is now registered
            {staffCount > 0 && ` with ${staffCount} teacher${staffCount !== 1 ? 's' : ''}`}
          </p>
        </motion.div>

        {/* School Admin Login Credentials */}
        {schoolCreds && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-success/5 border-2 border-success/20 p-6 rounded-2xl mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-success" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Your School Admin Login</h3>
                <p className="text-sm text-muted-foreground">Save these credentials, you'll need them to log in next time</p>
              </div>
            </div>
            <div className="space-y-2 p-4 bg-card rounded-xl border border-border">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground font-medium">Email:</span>
                <span className="font-mono font-semibold text-foreground">{schoolCreds.email}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground font-medium">Temp Password:</span>
                <div className="flex items-center gap-2">
                  <code className="px-2 py-1 bg-muted rounded font-mono font-bold tracking-wider text-foreground">
                    {showPassword ? schoolCreds.password : '•'.repeat(schoolCreds.password.length)}
                  </code>
                  <button
                    onClick={() => setShowPassword(v => !v)}
                    className="p-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => copyPassword(schoolCreds.password)}
                    className="p-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    title="Copy password"
                  >
                    {copiedPassword ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
              Save these credentials. You can change your password in Settings after logging in.
            </p>
          </motion.div>
        )}

        {/* What's next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-card p-6 rounded-2xl border border-border mb-8 text-left"
        >
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent" />
            What you can do now
          </h3>
          <div className="space-y-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm text-foreground">{feature.label}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* In demo, one line in place of a per-recipient invite list: what would
            have happened, without the rows nobody can act on. */}
        {isDemoMode() && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75 }}
            className="text-xs text-muted-foreground mb-8 flex items-start gap-2 text-left"
          >
            <Mail className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              In a real setup your {staffCount > 0 ? `${staffCount} teacher${staffCount !== 1 ? 's' : ''} and any ` : ''}
              additional contacts would each get an invite email now. Nothing is sent in
              demo. The inbox bottom-right shows what those emails look like.
            </span>
          </motion.div>
        )}

        {/* CTA */}
        {inviteWarnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-warning/10 border border-warning/30 text-warning-text text-sm p-4 rounded-lg mb-4 space-y-1"
          >
            <p className="font-semibold mb-1">Some invites couldn't be sent:</p>
            {inviteWarnings.map((w, i) => <p key={i}>• {w}</p>)}
          </motion.div>
        )}
        {submitError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg mb-4"
          >
            {submitError}
          </motion.div>
        )}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          onClick={handleGoToDashboard}
          disabled={isSubmitting}
          className="btn-accent w-full disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Setting up…
            </>
          ) : readyToContinue ? (
            <>
              Continue to Dashboard
              <ArrowRight className="w-5 h-5" />
            </>
          ) : (
            <>
              Go to Dashboard
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
}
